import type { PaymentRequest } from "@stripe/stripe-js";

import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useAtomValue, useSetAtom } from "jotai";
import { useEffect, useRef, useState } from "react";
import { ActivityIndicator, Pressable, StyleSheet, View } from "react-native";
import Svg, { Path } from "react-native-svg";
import * as v from "valibot";

import { eventMembersFamily } from "@/atoms/event-members";
import { sessionAtom } from "@/atoms/session";
import AddToCalendar from "@/components/add-to-calendar";
import CreditCardEntry from "@/components/credit-card-entry";
import { GText } from "@/components/g-text";
import AppModal from "@/components/modal";
import SignInModalContent from "@/components/sign-in/sign-in-modal-content";
import { colors } from "@/constants/colors";
import Bugsnag from "@/lib/bugsnag";
import { DisplayEvent } from "@/lib/event-helpers";
import { fetchSingleEventMember } from "@/lib/event-queries";
import { PaymentIntentResponseSchema } from "@/lib/schemas";
import { supabase } from "@/lib/supabase";
import { formatPriceViewMode } from "@/utils/copy";

const stripePromise = loadStripe(
  process.env.EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY!,
);

type Step =
  | "auth"
  | "closed"
  | "confirming"
  | "loading"
  | "payment"
  | "success"
  | "timeout";

export function usePayment(EVENT: DisplayEvent, myRsvpStatus: null | string) {
  const sessionState = useAtomValue(sessionAtom);
  const session = sessionState === "loading" ? null : sessionState;
  const setMembers = useSetAtom(eventMembersFamily(EVENT.id));
  const [step, setStep] = useState<Step>("closed");
  const [clientSecret, setClientSecret] = useState<null | string>(null);
  const [error, setError] = useState<null | string>(null);
  const [walletPaymentRequest, setWalletPaymentRequest] =
    useState<null | PaymentRequest>(null);

  async function startPayment() {
    console.log("starting payment");
    if (session) {
      beginPayment();
    } else {
      setStep("auth");
    }
  }

  async function beginPayment() {
    setStep("loading");
    setError(null);
    setWalletPaymentRequest(null);
    try {
      if (!session) {
        setError("Not authenticated");
        return;
      }

      const priceCents = (EVENT.priceCents ?? 0) + EVENT.priceServiceFee;

      const [paymentResult, walletResult] = await Promise.all([
        supabase.functions.invoke("create-payment-intent", {
          body: { eventId: EVENT.id, eventTicketKindId: EVENT.ticketKindId },
          headers: { Authorization: `Bearer ${session.access_token}` },
        }),
        stripePromise.then((stripe) => {
          if (!stripe) return null;
          const pr = stripe.paymentRequest({
            country: "US",
            currency: "usd",
            requestPayerName: true,
            total: { amount: priceCents, label: EVENT.title },
          });
          return pr.canMakePayment().then((result) => (result ? pr : null));
        }),
      ]);

      const { data, error: invokeError } = paymentResult;
      if (invokeError) {
        Bugsnag.notify(
          new Error(`Payment intent error: ${invokeError.message}`),
        );
        setError(invokeError.message ?? "Failed to start payment");
        return;
      }
      console.log("response info from payment", data);
      const { clientSecret: secret } = v.parse(
        PaymentIntentResponseSchema,
        data,
      );
      setClientSecret(secret);
      setWalletPaymentRequest(walletResult);
      setStep("payment");
    } catch (e) {
      Bugsnag.notify(e instanceof Error ? e : new Error(String(e)));
      setError("Failed to start payment. Please try again.");
    }
  }

  const [copied, setCopied] = useState(false);

  const timeoutRef = useRef<null | ReturnType<typeof setTimeout>>(null);

  function handleClose(success?: boolean) {
    if (success) {
      setStep("confirming");
      return;
    }
    setStep("closed");
    setClientSecret(null);
    setError(null);
  }

  useEffect(() => {
    if (step !== "confirming") return;

    const userId = session?.user.id;
    if (!userId) return;

    // If Realtime already delivered the update, skip polling
    if (myRsvpStatus === "confirmed") {
      setStep("success");
      return;
    }

    const intervalId = setInterval(async () => {
      const member = await fetchSingleEventMember(EVENT.id, userId);
      if (member?.rsvp_status === "confirmed") {
        setMembers((prev) =>
          prev.some((m) => m.user_id === member.user_id)
            ? prev.map((m) => (m.user_id === member.user_id ? member : m))
            : [...prev, member],
        );
        setStep("success");
      }
    }, 2_000);

    timeoutRef.current = setTimeout(() => {
      setStep("timeout");
    }, 15_000);

    return () => {
      clearInterval(intervalId);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [step, myRsvpStatus, EVENT.id, session?.user.id, setMembers]);

  function dismissModal() {
    setStep("closed");
    setClientSecret(null);
    setError(null);
  }

  const paymentUI = (
    <AppModal onClose={dismissModal} visible={step !== "closed"}>
      {step === "auth" && (
        <SignInModalContent onClose={dismissModal} onSignedIn={beginPayment} />
      )}

      {step === "loading" && (
        <View>
          <View style={styles.header}>
            <GText style={styles.modalHeading}>setting up payment</GText>
          </View>
          {error ? (
            <View>
              <GText style={styles.errorText}>{error}</GText>
              <Pressable onPress={dismissModal} style={styles.retryBtn}>
                <GText style={styles.retryBtnText}>Close</GText>
              </Pressable>
            </View>
          ) : (
            <View style={styles.spinnerContainer}>
              <ActivityIndicator color={colors.white} size="large" />
              <GText style={styles.loadingText}>setting up payment...</GText>
            </View>
          )}
        </View>
      )}

      {step === "payment" && clientSecret && (
        <Elements
          options={{
            appearance: {
              theme: "night",
              variables: {
                iconCardErrorColor: colors.red,
                iconColor: colors.white,
              },
            },
            clientSecret,
          }}
          stripe={stripePromise}
        >
          <CreditCardEntry
            clientSecret={clientSecret}
            onClose={(success) => handleClose(success)}
            paymentRequest={walletPaymentRequest}
            price={formatPriceViewMode(EVENT.priceCents, EVENT.priceServiceFee)}
          />
        </Elements>
      )}

      {step === "confirming" && (
        <View style={styles.spinnerContainer}>
          <ActivityIndicator color={colors.white} size="large" />
          <GText style={styles.loadingText}>confirming your ticket...</GText>
        </View>
      )}

      {step === "timeout" && (
        <View style={styles.successContainer}>
          <GText style={styles.errorText}>
            there was a timeout, please refresh the page
          </GText>
          <Pressable onPress={dismissModal} style={styles.retryBtn}>
            <GText style={styles.retryBtnText}>Close</GText>
          </Pressable>
        </View>
      )}

      {step === "success" && (
        <View style={styles.successContainer}>
          <Svg fill="none" height={50} viewBox="0 0 72 72" width={50}>
            <Path
              d="M22 36L32 46L50 28"
              stroke={colors.white}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={4}
            />
          </Svg>

          <GText style={styles.successHeading}>see you there</GText>
          <AddToCalendar
            description={EVENT.description}
            endISO={EVENT.endISO}
            geo={EVENT.geo}
            location={EVENT.address}
            startISO={EVENT.startISO}
            title={EVENT.title}
            venue={EVENT.venue}
          >
            <GText style={styles.calendarLink}>+ add to calendar</GText>
          </AddToCalendar>
          <Pressable
            onPress={() => {
              navigator.clipboard.writeText(window.location.href);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            }}
            style={styles.shareBtn}
          >
            <GText style={styles.inviteText}>and bring your friends!</GText>

            <View style={styles.linkRow}>
              <Svg
                fill={colors.white}
                height={16}
                viewBox="0 0 442.246 442.246"
                width={16}
              >
                <Path
                  d="M409.657,32.474c-43.146-43.146-113.832-43.146-156.978,0l-84.763,84.762c29.07-8.262,60.589-6.12,88.129,6.732l44.063-44.064c17.136-17.136,44.982-17.136,62.118,0c17.136,17.136,17.136,44.982,0,62.118l-55.386,55.386l-36.414,36.414c-17.136,17.136-44.982,17.136-62.119,0l-47.43,47.43c11.016,11.017,23.868,19.278,37.332,24.48c36.415,14.382,78.643,8.874,110.467-16.219c3.06-2.447,6.426-5.201,9.18-8.262l57.222-57.222l34.578-34.578C453.109,146.306,453.109,75.926,409.657,32.474z"
                  stroke={colors.white}
                />
                <Path
                  d="M184.135,320.114l-42.228,42.228c-17.136,17.137-44.982,17.137-62.118,0c-17.136-17.136-17.136-44.981,0-62.118l91.8-91.799c17.136-17.136,44.982-17.136,62.119,0l47.43-47.43c-11.016-11.016-23.868-19.278-37.332-24.48c-38.25-15.3-83.232-8.262-115.362,20.502c-1.53,1.224-3.06,2.754-4.284,3.978l-91.8,91.799c-43.146,43.146-43.146,113.832,0,156.979c43.146,43.146,113.832,43.146,156.978,0l82.927-83.845C230.035,335.719,220.243,334.496,184.135,320.114z"
                  stroke={colors.white}
                />
              </Svg>
              <GText style={styles.clipboardUrl}>
                {copied ? "copied to clipboard" : "copy link to clipboard"}
              </GText>
            </View>
          </Pressable>
        </View>
      )}
    </AppModal>
  );

  function showSuccess() {
    setStep("success");
  }

  return {
    loading: step === "loading" || step === "confirming",
    paymentUI,
    showSuccess,
    startPayment,
  };
}

const styles = StyleSheet.create({
  calendarLink: {
    color: colors.white,
    fontSize: 14,
    marginBottom: 24,
  },
  clipboardUrl: {
    color: colors.white,
    fontSize: 14,
    overflow: "hidden",
  },
  errorText: {
    color: colors.red,
    fontSize: 14,
    marginTop: 8,
  },
  header: {
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 16,
  },
  inviteText: {
    color: colors.white,
    fontSize: 18,
    marginBottom: 4,
    marginTop: 12,
  },
  linkRow: {
    alignItems: "center",
    flexDirection: "row",
    gap: 8,
  },
  loadingText: {
    color: colors.muted,
    fontSize: 14,
    marginTop: 16,
  },
  modalHeading: {
    color: colors.light,
    fontSize: 24,
    fontWeight: "bold",
  },
  retryBtn: {
    alignItems: "center",
    backgroundColor: colors.darkGray,
    borderRadius: 28,
    marginTop: 16,
    paddingVertical: 14,
    width: "100%",
  },
  retryBtnText: {
    color: colors.white,
    fontSize: 16,
    fontWeight: "600",
  },
  shareBtn: {
    alignItems: "center",
    backgroundColor: colors.cardBg,
    borderColor: colors.midGray,
    borderRadius: 999,
    borderWidth: 1,
    gap: 8,
    maxWidth: "100%",
    paddingHorizontal: 40,
    paddingVertical: 12,
  },
  spinnerContainer: {
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 40,
  },
  successContainer: {
    alignItems: "center",
    paddingBottom: 32,
    paddingTop: 16,
  },
  successHeading: {
    color: colors.light,
    fontSize: 28,
    fontWeight: "bold",
    marginBottom: 24,
  },
});
